// pi-steps.ts — the DOM-free turn/step model behind the Claude-desktop-style
// pi chat rendering. Partitions the flat pi transcript (user / assistant /
// toolResult / bashExecution messages) into rows: user bubbles and TURN
// GROUPS. Inside a turn, thinking and tool calls fold into collapsible step
// timelines ("Used …, ran 3 commands") and assistant text renders as prose IN
// PLACE — output text BREAKS the step timeline into segments, so the screen
// mirrors actual turn order: [thinking+tools] [text] [thinking+tools] [text].
// (Owner item #20: the old model kept ONE timeline per turn plus a single
// trailing answer, which merged post-text thinking into the pre-text group
// and re-bucketed earlier prose into the timeline as "notes".)
//
// Same lane as pi-live.ts: smoke transpiles this module standalone, so it
// must carry NO runtime imports (type-only imports are erased by esbuild).

import type { PiAttachmentBlock, PiBlock, PiMessage, PiToolCall } from './pi-live.js';

// ---- steps ----------------------------------------------------------------

export type StepCategory =
  | 'command'
  | 'read'
  | 'create'
  | 'edit'
  | 'search'
  | 'web'
  | 'fetch'
  | 'agent'
  | 'tasks'
  | 'message'
  | 'team'
  | 'other';

export interface PiStep {
  /** Stable across re-partitions: the toolCallId when there is one, else the
   * message/block address (m<idx>-b<i>) — expansion state keys off this. */
  key: string;
  kind: 'thinking' | 'tool';
  /** Collapsed one-line label — "Ran command", "Read config.ts",
   * "Thought for 2 min 14 sec", "Query warehouse". */
  label: string;
  /** Icon id from STEP_ICONS, or `letter:X` for a generic-tool letter badge. */
  icon: string;
  expandable: boolean;
  /** Epoch-ms timestamp of the message this step came from (pi stamps
   * per-message, not per-block — every step of a message shares its time).
   * Absent when the transcript carries no timestamp. */
  ts?: number;
  // thinking
  thinkingText?: string;
  thinkingMs?: number;
  streaming?: boolean;
  // tool
  cat?: StepCategory;
  toolId?: string;
  toolName?: string;
  args?: unknown;
  /** Result carried by a toolResult MESSAGE (the snapshot lane — live runs
   * read the model's toolRuns instead). */
  resultText?: string;
  /** The toolResult message's `details` seat, when present — a structured
   * payload beside the counted content text. */
  resultDetails?: unknown;
  isError?: boolean;
  /** bashExecution rows: the command/output pair pi records for HITL shells. */
  command?: string;
  output?: string;
}

/**
 * The `details` seat for a rendered step: the snapshot lane's own copy first
 * (transcript replay), then the live toolRun's `partial.details`.
 *
 * ⛔ NEVER KEY THIS ON A TOOL NAME. It was — `if (s.toolName === 'workflow')`,
 * written when `workflow` was the only tool carrying a `details` payload — and
 * that line silently starved the next two. `workflow_status` and
 * `workflow_cancel` shipped WITH cards, registered in TOOL_TREATMENTS, and
 * still rendered as raw Request/Response JSON boxes on a live VM: their card
 * saw `details: undefined`, threw, and renderToolDetail's catch degraded them
 * to the fallback. Every layer reported success; the name list was the only
 * thing that had not moved.
 *
 * A name list at a transport seat fails silently every time the list falls
 * behind, and the failure looks exactly like "no card was written".
 */
export function stepDetailsOf(
  step: Pick<PiStep, 'resultDetails'>,
  runPartial: unknown,
): unknown {
  if (step.resultDetails !== undefined) return step.resultDetails;
  if (!runPartial || typeof runPartial !== 'object') return undefined;
  return (runPartial as { details?: unknown }).details;
}

export interface PiUserRow {
  type: 'user';
  key: string;
  idx: number;
  msg: PiMessage;
}

// ---- async ask cards (owner item 46) ---------------------------------------

/** One option of an ask card (mirrors the tool schema). */
export interface PiAskOption {
  label: string;
  description: string;
}

/**
 * One QUESTION of an ask_user_question call — one card each, first-class in
 * the transcript flow (owner item 46: never lumped into the tool-step chips).
 * `qid` is parsed from the tool RESULT text (`Q-N: …` lines, tool-minted);
 * null while the result is still streaming or on the legacy blocking lane
 * whose result predates ids.
 */
export interface PiAskCard {
  /** Stable per re-partition: toolCallId + ordinal. */
  key: string;
  qid: string | null;
  header: string;
  question: string;
  multiSelect: boolean;
  options: PiAskOption[];
  /** The asking call, so an answer can re-supply the snapshot. */
  toolCallId: string;
  /** Legacy blocking lane only: the answer parsed from the tool result. */
  legacy?: PiAskLegacyAnswer;
}

export interface PiAskLegacyAnswer {
  chosen: string[];
  freeText?: string;
  skipped: boolean;
  answeredBy?: string;
}

/** A parsed <ask-answer id="Q-N"> user message → the right-aligned answer
 * card (one per answered question; sender/time ride the message itself). */
export interface PiAskAnswerRow {
  type: 'ask-answer';
  key: string;
  idx: number;
  msg: PiMessage;
  qid: string;
  header: string;
  chosen: string[];
  freeText?: string;
  skipped: boolean;
}

/** Detect + parse the injected answer envelope (one copy of the format —
 * the console's askAnswerMessage() writes it; smoke pins both directions). */
export function parseAskAnswer(text: string): Omit<PiAskAnswerRow, 'type' | 'key' | 'idx' | 'msg'> | null {
  const m = /^<ask-answer id="(Q-\d{1,6})">\n([\s\S]*?)\n<\/ask-answer>\s*$/.exec(text.trim());
  if (!m) return null;
  const lines = m[2]!.split('\n');
  const header = (lines[0] ?? '').split(' — ')[0] ?? '';
  const chosen: string[] = [];
  let freeText: string | undefined;
  let skipped = false;
  for (const line of lines.slice(1)) {
    if (line.startsWith('+ ')) chosen.push(line.slice(2));
    else if (line.startsWith('said: ')) freeText = line.slice(6);
    else if (line.startsWith('skipped')) skipped = true;
  }
  return { qid: m[1]!, header, chosen, ...(freeText ? { freeText } : {}), skipped };
}

/** Status scan: every answered qid in the transcript, with who/when from the
 * carrying message. Pure over rows — a reattach snapshot rebuild gets the
 * same statuses because it runs the same scan. */
export interface PiAskAnswerState {
  row: PiAskAnswerRow;
  answeredBy: string | null;
  answeredAt: number | null;
}
export function collectAskAnswers(rows: PiRow[]): Map<string, PiAskAnswerState> {
  const out = new Map<string, PiAskAnswerState>();
  for (const r of rows) {
    if (r.type !== 'ask-answer') continue;
    const sender = (r.msg as { sender?: { name?: string } }).sender;
    const ts = normTs((r.msg as { timestamp?: unknown }).timestamp);
    // Last answer wins (a re-answer supersedes; the earlier card still shows).
    out.set(r.qid, {
      row: r,
      answeredBy: typeof sender?.name === 'string' && sender.name ? sender.name : null,
      answeredAt: ts || null,
    });
  }
  return out;
}

// ---- escalation lifecycle cards (escalation-lifecycle package) --------------
//
// Two lanes feed these:
//   LANE A — this session RAISED it: a successful `escalate` tool call
//   becomes one first-class PiEscCard in the turn flow (never a step chip);
//   the tool RESULT receipt fills the runtime id + minted corpus ESC id.
//   A REFUSED escalate (isError / "escalate: …") keeps its step chip — a
//   refusal wrote nothing, so there is no lifecycle to card.
//   LANE B — lifecycle events ARRIVING here: a `<coordination …>` envelope
//   whose event is one of ESC_EVENTS becomes a PiEscEventRow (left-aligned
//   event card), parsed from PiMessage.coord (pi-live preserves the attrs at
//   ingest). Every other coordination event stays a plain bubble — that is a
//   smoke CONTROL, not an omission. Older brokers omit the esc_* attrs and
//   may lack the newer event names entirely: unknown = no card, plain
//   rendering, never a throw.

/** One raised escalation — first-class in the transcript flow. */
export interface PiEscCard {
  /** Stable per re-partition: toolCallId-based. */
  key: string;
  /** Display id: the corpus ESC-NNN when minted, else the runtime esc-uuid,
   * null while the receipt is still streaming. */
  escId: string | null;
  /** The runtime record id (esc-xxxxxxxx) — resolve_escalation targets it. */
  runtimeId: string | null;
  /** The precise decision needed (the `question` arg, falling back to
   * `summary`). */
  question: string;
  severity: 'blocker' | 'question';
  options: string[];
  resolutionCriterion: string | null;
  /** Full context from the raise (expanded view). */
  detail: string;
  /** Corpus record path — null at raise time (a later lifecycle event may
   * carry esc_path; click-through rides the ESC id either way). */
  corpusPath: string | null;
  toolCallId: string;
}

/** The escalation lifecycle events the transcript renders as cards. */
export const ESC_EVENTS = [
  'escalation',
  'escalation_assigned',
  'escalation_answered',
  'escalation_resolved',
] as const;
export type PiEscEvent = (typeof ESC_EVENTS)[number];

/** A parsed escalation lifecycle envelope → a left-aligned event card row. */
export interface PiEscEventRow {
  type: 'esc-event';
  key: string;
  idx: number;
  msg: PiMessage;
  event: PiEscEvent;
  /** Every id the envelope's `items` attr names (usually one ESC-NNN). */
  escIds: string[];
  /** esc_* card attrs — ALL optional (older brokers omit them; the card
   * degrades to id-only). */
  title: string | null;
  path: string | null;
  holder: string | null;
  severity: string | null;
  escClass: string | null;
  /** The full payload text ("cause: …") — kept verbatim in the expandable
   * detail; the card never destroys it. */
  cause: string;
  /** A reopen arrives as escalation_assigned with REOPENED in the cause. */
  reopened: boolean;
}

/** The wake executor's boot/resume worklist pull — a user message whose
 * ENTIRE trimmed text is exactly `linkup`. Renders as a muted system chip,
 * never a full bubble. A message merely CONTAINING the word stays a bubble
 * (control). */
export interface PiLinkupRow {
  type: 'linkup';
  key: string;
  idx: number;
  msg: PiMessage;
}

// ---- prime-agent harness refinement cards (owner ruling 2026-09-28) --------
//
// prime-agent's continual harness writes each refinement result into the
// session as a DISPLAYED custom message — `customType: "refinement_outcome"`,
// `content: "Refinement complete: <summary>"`, `details: { refinementId,
// summary, scope, rollbackOf?, edits[] }` (the fork's
// REFINEMENT_OUTCOME_CUSTOM_TYPE; its model-facing twin `refinement_notice`
// is display:false and stays hidden). Like every displayed custom it
// projects to a `role: "user"` row, so it rendered as a right-aligned user
// bubble and took the operator's sender chip by tail alignment. It is the
// HARNESS talking, never the owner: the row builder keys on the harness's own
// customType (never on the text prefix) and emits a left-aligned, collapsed
// "Harness Refined" card row instead. Inlined here (and mirrored in pi-live
// isHarnessRefinement) because the smoke transpiles this file standalone;
// harness-refined-card.test.mjs pins the two copies equal.

/** The harness's own marker for a refinement result (prime-agent fork). */
export const HARNESS_REFINED_CUSTOM_TYPE = 'refinement_outcome';

/** One applied edit, as the card's expanded detail lists it. The full
 * before/after entry bodies are deliberately NOT carried: the edit's own
 * content is the text that changed. */
export interface PiHarnessEdit {
  action: string;
  kind: string;
  id: string;
  title: string;
  path: string;
  reason: string;
  content: string;
  applied: boolean | null;
}

/** A harness refinement result → the left-aligned "Harness Refined" card. */
export interface PiHarnessRefinedRow {
  type: 'harness-refined';
  key: string;
  idx: number;
  msg: PiMessage;
  /** The full message text ("Refinement complete: …"), verbatim. */
  text: string;
  summary: string;
  refinementId: string | null;
  scope: string | null;
  rollbackOf: string | null;
  edits: PiHarnessEdit[];
}

/** True for the harness's refinement result — keyed on the structured
 * customType only (a user who TYPES "Refinement complete: …" stays a user). */
export function isHarnessRefined(m: unknown): boolean {
  return !!m && typeof m === 'object' && (m as { customType?: unknown }).customType === HARNESS_REFINED_CUSTOM_TYPE;
}

/** The card fields of a refinement message; tolerant of a missing or
 * malformed `details` (the card then carries the text alone). */
export function harnessRefinedOf(m: { content?: unknown; details?: unknown }): Omit<PiHarnessRefinedRow, 'type' | 'key' | 'idx' | 'msg'> {
  const text = textOf(m.content);
  const d = m.details && typeof m.details === 'object' ? (m.details as Record<string, unknown>) : {};
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const summary = str(d.summary) || text.replace(/^Refinement complete:\s*/, '');
  const edits: PiHarnessEdit[] = (Array.isArray(d.edits) ? d.edits : [])
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .map((e) => ({
      action: str(e.action),
      kind: str(e.kind),
      id: str(e.id),
      title: str(e.title),
      path: str(e.path),
      reason: str(e.reason),
      content: str(e.content),
      applied: typeof e.applied === 'boolean' ? e.applied : null,
    }));
  return {
    text,
    summary,
    refinementId: str(d.refinementId) || null,
    scope: str(d.scope) || null,
    rollbackOf: str(d.rollbackOf) || null,
    edits,
  };
}

// ---- prime-agent system outcome cards (owner ruling 2026-09-30) ------------
//
// Besides the refinement result, the fork writes three more DISPLAYED outcome
// customs (its messages.ts: COMPACTION_OUTCOME_CUSTOM_TYPE,
// MCP_CONNECTION_OUTCOME_CUSTOM_TYPE, SESSION_SLASH_COMMAND_RESULT_CUSTOM_TYPE).
// Each projects to a `role: "user"` row exactly like the refinement did, so
// each rendered as the operator's own right-aligned bubble. They are the
// HARNESS reporting, never a person: keyed on the customType only (a person
// who TYPES the same words stays a user bubble), each becomes a left-aligned,
// collapsed card on the Harness Refined shell. `details`, as the fork's
// producers write it:
//   compaction_outcome            { reason: threshold|overflow|requested,
//                                   outcome: skipped|cancelled|failed }
//   mcp_connection_outcome        connect: { kind?: "connect", label, source:
//                                   login|paste|retry, verification:
//                                   connected|unverified|unsaved, toolCount?,
//                                   issue?, issueCategory?, connectionId?,
//                                   addedAccount?, activation? }
//                                 disconnect: { kind: "disconnect", label,
//                                   removal, connectionId?, activation? }
//   session_slash_command_result  { command: { name, args, text }, success,
//                                   severity: info|warning|error, error?,
//                                   commandEntryId? }
// A compaction OUTCOME is never a success (a completed compaction writes a
// compactionSummary instead), so its title names the operation, not a result.
// The typed command itself (`session_slash_command`, content = the person's
// text) is the person's own words and stays a user bubble. Inlined here and
// mirrored in pi-live SYSTEM_OUTCOME_CUSTOM_TYPES (standalone smoke
// transpile); system-outcome-card.test.mjs pins the two sets equal.

/** customType → the card title (one per displayed fork outcome). */
export const SYSTEM_OUTCOME_TITLES: Readonly<Record<string, string>> = {
  compaction_outcome: 'Context Compaction',
  mcp_connection_outcome: 'MCP Connection',
  session_slash_command_result: 'Command Result',
};

export type PiOutcomeStatus = 'ok' | 'warn' | 'error';

/** A fork system outcome → a left-aligned, collapsed system card. */
export interface PiSystemOutcomeRow {
  type: 'system-outcome';
  key: string;
  idx: number;
  msg: PiMessage;
  customType: string;
  title: string;
  /** The full message text, verbatim. */
  text: string;
  /** Its first line — the collapsed card's one-line summary. */
  summary: string;
  /** Short facts beside the title (e.g. "failed · overflow"). */
  meta: string;
  status: PiOutcomeStatus | null;
  /** Label/value pairs the expanded detail lists (empty values dropped). */
  fields: Array<[string, string]>;
}

/** True for a displayed fork system outcome — keyed on customType only. */
export function isSystemOutcome(m: unknown): boolean {
  if (!m || typeof m !== 'object') return false;
  const t = (m as { customType?: unknown }).customType;
  return typeof t === 'string' && Object.prototype.hasOwnProperty.call(SYSTEM_OUTCOME_TITLES, t);
}

/** The card fields of a system outcome; tolerant of a missing or malformed
 * `details` (the card then carries the text alone). */
export function systemOutcomeOf(m: {
  content?: unknown;
  details?: unknown;
  customType?: unknown;
}): Omit<PiSystemOutcomeRow, 'type' | 'key' | 'idx' | 'msg'> {
  const customType = typeof m.customType === 'string' ? m.customType : '';
  const text = textOf(m.content);
  const d = m.details && typeof m.details === 'object' ? (m.details as Record<string, unknown>) : {};
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const summary = (text.split('\n').find((l) => l.trim()) ?? '').trim();
  const fields: Array<[string, string]> = [];
  const put = (label: string, v: string): void => {
    if (v) fields.push([label, v]);
  };
  let meta: string[] = [];
  let status: PiOutcomeStatus | null = null;
  if (customType === 'compaction_outcome') {
    const outcome = str(d.outcome);
    const reason = str(d.reason);
    put('Outcome', outcome);
    put('Reason', reason);
    meta = [outcome, reason];
    status = outcome === 'failed' ? 'error' : outcome ? 'warn' : null;
  } else if (customType === 'mcp_connection_outcome') {
    const disconnect = d.kind === 'disconnect';
    const label = str(d.label);
    put('Service', label);
    put('Action', disconnect ? 'disconnect' : 'connect');
    if (disconnect) {
      put('Removal', str(d.removal));
      meta = [label, 'disconnected'];
      status = str(d.removal) ? 'ok' : null;
    } else {
      const v = str(d.verification);
      put('Verification', v);
      put('Source', str(d.source));
      put('Tools verified', typeof d.toolCount === 'number' ? String(d.toolCount) : '');
      put('Issue', str(d.issue));
      put('Issue category', str(d.issueCategory));
      put('Added account', d.addedAccount === true ? 'yes' : '');
      meta = [label, v];
      status = v === 'connected' ? 'ok' : v ? 'warn' : null;
    }
    put('Account', str(d.connectionId));
    put('Activation', str(d.activation));
    if (d.activation === 'inactive' && status === 'ok') status = 'warn';
  } else if (customType === 'session_slash_command_result') {
    const cmd = d.command && typeof d.command === 'object' ? (d.command as Record<string, unknown>) : {};
    const cmdText = str(cmd.text) || (str(cmd.name) ? `/${str(cmd.name)}${str(cmd.args) ? ` ${str(cmd.args)}` : ''}` : '');
    const sev = str(d.severity);
    const success = typeof d.success === 'boolean' ? d.success : null;
    put('Command', cmdText);
    put('Result', success === null ? '' : success ? 'succeeded' : 'failed');
    put('Severity', sev);
    put('Error', str(d.error));
    meta = [cmdText, success === null ? '' : success ? 'succeeded' : 'failed'];
    status = sev === 'error' || success === false ? 'error' : sev === 'warning' ? 'warn' : success === true ? 'ok' : null;
  }
  return {
    customType,
    title: SYSTEM_OUTCOME_TITLES[customType] ?? 'System',
    text,
    summary,
    meta: meta.filter(Boolean).join(' · '),
    status,
    fields,
  };
}

// ---- prime-agent system notices (owner ruling 2026-10-01) ----------------
//
// The fork's TUI renders EVERY displayed custom message (role "custom") as a
// third category — inline in the agent's output, in its own muted/accent
// style — and never as a user message (interactive-mode.ts
// addMessageToChat). Our readers project a displayed custom to a user-role
// row that keeps its customType; that customType is the marker. Measured on a
// live release session: `ipython_state_restored` ("[python-state-restored]
// … These names are available again: …", details { restored }) rendered as
// the operator's right-aligned bubble. Exempt (stay what they are): the
// person-carrying customs (coordination-inbox — people's sends; the typed
// session_slash_command), agent_message (its own renderer), the harness
// cards above, and any row carrying a sender or coord attrs. Keyed on the
// structured customType only: a person who TYPES "[python-state-restored]"
// stays a user bubble. Inlined; pi-live SYSTEM_NOTICE_EXEMPT_CUSTOM_TYPES is
// the twin (system-notice.test.mjs pins them equal). pi-system-notice.ts
// renders the row.

export const SYSTEM_NOTICE_EXEMPT: readonly string[] = ['coordination-inbox', 'session_slash_command', 'agent_message'];

/** A displayed harness custom → an inline system-notice row. */
export interface PiSystemNoticeRow {
  type: 'system-notice';
  key: string;
  idx: number;
  msg: PiMessage;
  customType: string;
}

/** True for a displayed harness custom the Helm renders as a system notice. */
export function isSystemNotice(m: unknown): boolean {
  if (!m || typeof m !== 'object') return false;
  const r = m as { role?: unknown; customType?: unknown; sender?: unknown; coord?: unknown };
  if (r.role !== 'user' && r.role !== 'custom') return false;
  const t = r.customType;
  if (typeof t !== 'string' || !t || r.sender || r.coord) return false;
  return !SYSTEM_NOTICE_EXEMPT.includes(t) && !isHarnessRefined(m) && !isSystemOutcome(m);
}

/** escalate tool-result receipt, parsed. One copy of the format read
 * (pi-shared/extensions/agent-teams/escalation.ts writes it):
 *   "Escalated as esc-xxxxxxxx — minted ESC-NNN held by the epic-lead …"
 *   "Re-escalated esc-xxxxxxxx …" (bump)
 *   "escalate: you are this session's root …" (refusal, isError) */
export interface EscalateReceipt {
  refused: boolean;
  runtimeId: string | null;
  corpusEsc: string | null;
}
export function parseEscalateReceipt(text: string, isError: boolean): EscalateReceipt {
  const t = String(text ?? '');
  const refused = isError === true || /^escalate:/.test(t.trim());
  return {
    refused,
    runtimeId: refused ? null : (/\besc-[0-9a-f]{6,12}\b/.exec(t)?.[0] ?? null),
    corpusEsc: refused ? null : (/\bESC-\d{1,6}\b/.exec(t)?.[0] ?? null),
  };
}

/** Build the esc card from an escalate call's args (either dialect — object
 * or JSON string). Null on malformed args (no question and no summary): the
 * caller keeps the step chip so the call stays visible. */
function escCardFrom(args: unknown, callId: string): PiEscCard | null {
  const rec = argRecord(args);
  if (!rec) return null;
  const s = (k: string): string => (typeof rec[k] === 'string' ? (rec[k] as string).trim() : '');
  const question = s('question') || s('summary');
  if (!question) return null;
  const opts = Array.isArray(rec.options) ? rec.options : [];
  return {
    key: `${callId}-esc`,
    escId: null,
    runtimeId: null,
    question,
    severity: s('severity') === 'question' ? 'question' : 'blocker',
    options: opts.map((o) => String(o ?? '')).filter(Boolean),
    resolutionCriterion: s('resolution_criterion') || null,
    detail: s('detail'),
    corpusPath: null,
    toolCallId: callId,
  };
}

/** Fold the escalate RECEIPT into its card (ids arrive on the result). */
function fillEscCard(card: PiEscCard, receipt: EscalateReceipt): void {
  card.runtimeId = receipt.runtimeId;
  card.escId = receipt.corpusEsc ?? receipt.runtimeId;
}

/** Parse a preserved coordination envelope into event-row fields, or null
 * when it is not an escalation lifecycle event (unblocked, gate_pending,
 * question, … — the plain-bubble controls) or names no items. Defensive on
 * every attr: version skew must degrade, never throw. */
export function escEventFromCoord(
  coord: Record<string, string> | undefined,
  cause: string,
): Omit<PiEscEventRow, 'type' | 'key' | 'idx' | 'msg'> | null {
  if (!coord || typeof coord !== 'object') return null;
  const event = coord.event;
  if (event !== 'escalation' && event !== 'escalation_assigned'
    && event !== 'escalation_answered' && event !== 'escalation_resolved') return null;
  const escIds = String(coord.items ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter((x) => /^ESC-\d{1,6}$/.test(x));
  if (!escIds.length) return null;
  const attr = (k: string): string | null => {
    const v = coord[k];
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  };
  return {
    event,
    escIds,
    title: attr('esc_title'),
    path: attr('esc_path'),
    holder: attr('esc_holder'),
    severity: attr('esc_severity'),
    escClass: attr('esc_class'),
    cause: String(cause ?? ''),
    reopened: event === 'escalation_assigned' && /\bREOPENED\b/.test(String(cause ?? '')),
  };
}

/** The evidence line a resolved event's cause carries ("evidence: …") —
 * null when absent. One copy of the parse (scan + card detail both use it). */
export function escEvidence(cause: string): string | null {
  return /\bevidence:\s*([^\n]+)/.exec(String(cause ?? ''))?.[1]?.trim() ?? null;
}

/** One escalation id's LATEST lifecycle sighting in the transcript scan.
 * `order` is the transcript position that produced it — later wins. */
export interface PiEscState {
  order: number;
  /** A lifecycle event, or 'resolve_tool' for a resolve_escalation call in
   * THIS transcript (decision recorded → Answered). */
  event: PiEscEvent | 'resolve_tool';
  reopened: boolean;
  /** From a resolved event's cause ("evidence: …"), when present. */
  evidence: string | null;
  path: string | null;
  title: string | null;
}

/** Status scan: the latest lifecycle sighting per escalation id — from
 * esc-event rows AND resolve_escalation tool steps (their args.id is the
 * runtime id). Pure over rows, same reattach-rebuild property as
 * collectAskAnswers. */
export function collectEscEvents(rows: PiRow[]): Map<string, PiEscState> {
  const out = new Map<string, PiEscState>();
  let order = 0;
  const put = (id: string, st: Omit<PiEscState, 'order'>): void => {
    order += 1;
    const prev = out.get(id);
    if (!prev || prev.order < order) {
      out.set(id, {
        order,
        ...st,
        // esc_path/title/evidence stick once seen (a later id-only event
        // must not erase where the record lives).
        evidence: st.evidence ?? prev?.evidence ?? null,
        path: st.path ?? prev?.path ?? null,
        title: st.title ?? prev?.title ?? null,
      });
    }
  };
  for (const r of rows) {
    if (r.type === 'esc-event') {
      const evidence = r.event === 'escalation_resolved' ? escEvidence(r.cause) : null;
      for (const id of r.escIds) {
        put(id, { event: r.event, reopened: r.reopened, evidence, path: r.path, title: r.title });
      }
      continue;
    }
    if (r.type !== 'turn') continue;
    for (const g of r.segments) {
      for (const s of g.steps) {
        if (s.kind !== 'tool' || s.toolName !== 'resolve_escalation') continue;
        // Only a SUCCESSFUL resolve records a decision; an error result
        // ("No escalation …") changed nothing.
        if (s.isError === true) continue;
        const id = argString(s.args, ['id']);
        if (id) put(id, { event: 'resolve_tool', reopened: false, evidence: null, path: null, title: null });
      }
    }
  }
  return out;
}

/** REL-097 — the machine-written REFUSAL FOOTER the Bridge appends to an
 * RM reply (helm-bridge rm-refusal-ledger.ts). It arrives as an ordinary
 * assistant TEXT block whose first line starts with the sentinel, and it is
 * partitioned OUT of the prose here so it can never be mistaken for
 * something the model chose to write — the whole point is that the model
 * could not omit or reword it. A local copy of the sentinel: this module
 * must stay import-free for the standalone smoke transpile (see textOf's
 * note), and refusal-footer-drift is pinned by pi-steps' own suite. */
export const REFUSAL_FOOTER_SENTINEL = 'Refusals this turn:';

export interface PiRefusalCard {
  key: string;
  /** How many refusals the footer names. */
  count: number;
  /** `<tool> [<marker>] <measured line>`, one per refusal. */
  entries: string[];
  /** The footer verbatim — what an operator copies. */
  raw: string;
}

/** Parse a refusal footer text block. Returns null for anything that is not
 * one, which is the positive control every caller needs: ordinary prose
 * must never become a refusal card. */
export function refusalCardFrom(text: string, key: string): PiRefusalCard | null {
  const raw = String(text ?? '');
  const lines = raw.split('\n');
  const head = (lines[0] ?? '').trim();
  if (!head.startsWith(REFUSAL_FOOTER_SENTINEL)) return null;
  const m = /^Refusals this turn:\s*(\d+)\b/.exec(head);
  if (!m) return null;
  const entries = lines
    .slice(1)
    .map((l) => l.trim())
    .filter((l) => l.startsWith('- '))
    .map((l) => l.slice(2));
  return { key, count: Number(m[1]), entries, raw };
}

export interface PiErrorRow {
  type: 'error';
  key: string;
  idx: number;
  msg: PiMessage;
}

/** One [steps timeline][prose] section of a turn. Steps always precede the
 * section's text: a text run CLOSES the segment, and any later step opens the
 * next one — that break is the owner-item-#20 contract. */
export interface PiTurnSegment {
  /** Stable across re-partitions: turn key + ordinal. Segments only ever
   * append (messages append; a settled segment never re-splits), so the
   * ordinal is durable — group expansion state keys off this. */
  key: string;
  steps: PiStep[];
  /** The prose run that closes this segment — [] while it is steps-only. */
  textBlocks: PiBlock[];
  /** Message the prose came from (the LAST one feeds the turn's meta footer). */
  textMsg?: PiMessage;
  /** Owner item 46: ask cards render FIRST-CLASS in the turn flow (after the
   * segment's steps/prose, like text they CLOSE the segment). [] elsewhere. */
  asks: PiAskCard[];
  /** Escalation raise cards — same first-class contract as asks (a raise
   * CLOSES the segment; never a tool-step chip). [] elsewhere. */
  escs: PiEscCard[];
  /** REL-097 — the Bridge's machine-written refusal footer(s) for this turn.
   * Like asks/escs they are first-class and CLOSE the segment; unlike them
   * the model never authored the text. [] on every turn that refused
   * nothing — which is most of them. */
  refusals: PiRefusalCard[];
  /** P22 lane u4 — the Helm server's display cards ABOUT this turn (the §8f
   * refusal, the §8k failure), in arrival order. Like refusals they are the
   * host's words, never the model's; unlike them they CLOSE the segment, so a
   * later step renders after the card. Absent on every turn that got none. */
  cards?: PiHostCard[];
}

/** One Helm-server display card (pi-live's `aisdlc-card` message). */
export interface PiHostCard {
  key: string;
  mime: string;
  data: unknown;
}

/** A host card that landed before any turn existed — its own row, never a
 * turn (it carries no transcript ordinal). */
export interface PiAisdlcCardRow extends PiHostCard {
  type: 'aisdlc-card';
  idx: number;
  msg: PiMessage;
}

/** Host cards a turn row carries (pi-view rebuilds a settled row when this moves). */
export function hostCardCount(t: PiTurnRow): number {
  let n = 0;
  for (const g of t.segments) n += g.cards?.length ?? 0;
  return n;
}

export interface PiTurnRow {
  type: 'turn';
  key: string;
  firstIdx: number;
  lastIdx: number;
  /** Ordered [steps][text][steps][text]… — assistant text renders in place
   * and BREAKS the step timeline, so on-screen order mirrors actual turn
   * order. Never re-buckets: prose stays prose through every later event. */
  segments: PiTurnSegment[];
  stoppedEarly?: boolean;
  attachments: PiAttachmentBlock[];
}

export type PiRow =
  | PiUserRow
  | PiErrorRow
  | PiTurnRow
  | PiAskAnswerRow
  | PiEscEventRow
  | PiLinkupRow
  | PiAisdlcCardRow
  | PiHarnessRefinedRow
  | PiSystemOutcomeRow
  | PiSystemNoticeRow;

/** Text of a block array (local copy of pi-live's contentText — this module
 * must stay import-free for the standalone smoke transpile). */
function textOf(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content
    .map((b) => ((b as PiBlock)?.type === 'text' ? ((b as PiBlock).text ?? '') : ''))
    .join('');
}

function argRecord(args: unknown): Record<string, unknown> | null {
  try {
    const obj = typeof args === 'string' ? (JSON.parse(args) as unknown) : args;
    return typeof obj === 'object' && obj !== null ? (obj as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** First non-empty string among `keys` in the (possibly JSON-encoded) args —
 * shared with pi-view's step-detail rendering. */
export function argString(args: unknown, keys: string[]): string {
  const rec = argRecord(args);
  if (!rec) return '';
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Message timestamp → epoch ms (defensive: pi stamps epoch ms; treat small
 * values as seconds — same rule as the message meta footer). 0 when absent. */
function normTs(t: unknown): number {
  if (typeof t !== 'number' || !Number.isFinite(t) || t <= 0) return 0;
  return t < 1e12 ? t * 1000 : t;
}

function baseName(p: string): string {
  const clean = p.replace(/[\\/]+$/, '');
  const i = Math.max(clean.lastIndexOf('/'), clean.lastIndexOf('\\'));
  return i >= 0 ? clean.slice(i + 1) : clean;
}

function trunc(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** 'query_warehouse' / 'queryWarehouse' / 'mcp__crm__query_warehouse' →
 * "Query warehouse" (the Claude-desktop generic-tool label). */
export function prettyToolName(name: string): string {
  let n = name;
  const mcp = /^mcp__[^_]+(?:_[^_]+)*__(.+)$/.exec(n);
  if (mcp?.[1]) n = mcp[1];
  n = n
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!n) return name;
  const lower = n.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Which family a tool name belongs to — drives icon, label, and the summary
 * phrase. The generous regexes are the net for pi core tools plus common
 * harness spellings (prime-agent's one wire tool, `ipython`, lands on 'other').
 * OR-46: the EXACT-name table that sat in front of this ladder named only the
 * four deleted pi extensions' tools and the RM toolbelt; it left with them. */
export function classifyTool(name: string): StepCategory {
  const n = name.toLowerCase();
  if (/bash|shell|terminal|(^|_)exec|run_command/.test(n)) return 'command';
  if (/web_?search/.test(n)) return 'web';
  if (/fetch|http|browse|curl/.test(n)) return 'fetch';
  if (/^read|read_file|readfile|(^|_)cat($|_)|view_file|open_file/.test(n)) return 'read';
  if (/write|create/.test(n)) return 'create';
  if (/edit|str_replace|patch|apply/.test(n)) return 'edit';
  if (/grep|glob|(^|_)find|(^|_)search|^ls$|list_files|rip?grep/.test(n)) return 'search';
  if (/agent|task|subagent|spawn/.test(n)) return 'agent';
  if (/todo|plan/.test(n)) return 'tasks';
  return 'other';
}

const CAT_ICON: Record<StepCategory, string> = {
  command: 'terminal',
  read: 'file',
  create: 'filePlus',
  edit: 'pencil',
  search: 'search',
  web: 'globe',
  fetch: 'globe',
  agent: 'agents',
  tasks: 'tasks',
  message: 'chat',
  team: 'agents',
  other: '', // letter badge, filled per tool
};

/** Collapsed step label for a tool call — "Ran command", "Read config.ts",
 * "Created report.html", "Query warehouse". */
export function toolLabel(name: string, args: unknown): string {
  const cat = classifyTool(name);
  const file = baseName(argString(args, ['path', 'file_path', 'filename', 'file']));
  switch (cat) {
    case 'command':
      return 'Ran command';
    case 'read':
      return file ? `Read ${trunc(file, 48)}` : 'Read file';
    case 'create':
      return file ? `Created ${trunc(file, 48)}` : 'Created file';
    case 'edit':
      return file ? `Edited ${trunc(file, 48)}` : 'Edited file';
    case 'search': {
      const pat = argString(args, ['pattern', 'query', 'q']);
      return pat ? `Searched for ${trunc(pat, 40)}` : 'Searched files';
    }
    case 'web': {
      const q = argString(args, ['query', 'q']);
      return q ? `Searched the web for ${trunc(q, 40)}` : 'Searched the web';
    }
    case 'fetch': {
      const url = argString(args, ['url', 'uri']);
      const host = /^[a-z]+:\/\/([^/]+)/i.exec(url)?.[1] ?? '';
      return host ? `Fetched ${trunc(host, 40)}` : 'Fetched page';
    }
    case 'agent':
      return 'Ran agent';
    case 'tasks':
      return 'Updated tasks';
    case 'message':
      return 'Sent a message';
    case 'team':
      return 'Team action';
    default:
      return prettyToolName(name);
  }
}

export function toolIcon(name: string): string {
  const cat = classifyTool(name);
  if (cat !== 'other') return CAT_ICON[cat];
  const pretty = prettyToolName(name);
  return `letter:${(pretty.charAt(0) || '?').toUpperCase()}`;
}

/** "2 min 14 sec" / "42 sec" — the user-facing thought duration. */
export function fmtThinkTime(ms: number): string {
  const s = Math.max(1, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m} min ${s % 60} sec` : `${s} sec`;
}

export function thinkingLabel(step: Pick<PiStep, 'streaming' | 'thinkingMs'>): string {
  if (step.streaming) return 'Thinking…';
  return typeof step.thinkingMs === 'number' ? `Thought for ${fmtThinkTime(step.thinkingMs)}` : 'Thought about it';
}

// ---- step builders --------------------------------------------------------

export function thinkingStep(b: PiBlock, key: string): PiStep {
  const step: PiStep = {
    key,
    kind: 'thinking',
    icon: 'spark',
    expandable: true,
    thinkingText: b.thinking ?? '',
    streaming: b.streaming === true,
    label: '',
  };
  if (typeof b.thinkingMs === 'number') step.thinkingMs = b.thinkingMs;
  step.label = thinkingLabel(step);
  return step;
}

export function toolStep(call: { id?: string; name?: string; args?: unknown }, fallbackKey: string): PiStep {
  const name = call.name || 'tool';
  return {
    key: call.id || fallbackKey,
    kind: 'tool',
    cat: classifyTool(name),
    icon: toolIcon(name),
    label: toolLabel(name, call.args),
    expandable: true,
    toolId: call.id,
    toolName: name,
    args: call.args,
  };
}

/** Build ask cards from an ask_user_question call's args (either dialect —
 * object or JSON string). Empty on malformed args: the caller then keeps the
 * step chip so the call stays visible. */
function askCardsFrom(args: unknown, callId: string): PiAskCard[] {
  const rec = argRecord(args);
  const raw = rec?.questions;
  if (!Array.isArray(raw)) return [];
  const cards: PiAskCard[] = [];
  raw.forEach((q, i) => {
    const qr = (q ?? {}) as Record<string, unknown>;
    const question = String(qr.question ?? '').trim();
    if (!question) return;
    const opts = Array.isArray(qr.options) ? qr.options : [];
    cards.push({
      key: `${callId}-q${i}`,
      qid: typeof qr.id === 'string' && /^Q-\d{1,6}$/.test(qr.id) ? qr.id : null,
      header: String(qr.header ?? '').trim() || `Question ${i + 1}`,
      question,
      multiSelect: qr.multiSelect === true,
      options: opts.map((o) => {
        const or = (o ?? {}) as Record<string, unknown>;
        return { label: String(or.label ?? ''), description: String(or.description ?? '') };
      }).filter((o) => o.label),
      toolCallId: callId,
    });
  });
  return cards;
}

/**
 * Fold the ask TOOL RESULT into its cards:
 *   - async lane: `registered N…` + one `Q-N: …` line per question, in call
 *     order — those are the tool-minted ids;
 *   - legacy blocking lane: `answered n/m… by NAME:` + per-question blocks —
 *     parsed into `legacy` so old transcripts still show their outcomes.
 * Error results (legacy timeout / all-skipped / refused registration) leave
 * the cards Open — an error is never an answer.
 */
function fillAskCards(cards: PiAskCard[], result: string, isError: boolean): void {
  if (!result) return;
  const idMatches = [...result.matchAll(/^(Q-\d{1,6}): /gm)].map((m) => m[1]!);
  if (idMatches.length) {
    cards.forEach((c, i) => {
      if (idMatches[i]) c.qid = idMatches[i]!;
    });
    return;
  }
  if (isError) return;
  const head = /^answered \d+\/\d+ question(?:s)?(?: by (.+?))?:/.exec(result);
  if (!head) return;
  const answeredBy = head[1];
  for (const c of cards) {
    // The legacy result echoes `${header} — ${question}` then indented lines.
    const esc = `${c.header} — ${c.question}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const block = new RegExp(`^${esc}\\n((?: {2}.*\\n?)*)`, 'm').exec(result);
    if (!block) continue;
    const body = block[1] ?? '';
    const chosen = /^ {2}chose: (.+)$/m.exec(body)?.[1]?.split(', ') ?? [];
    const freeText = /^ {2}said: (.+)$/m.exec(body)?.[1];
    const skipped = / {2}skipped \(NOT decided\)/.test(body);
    c.legacy = { chosen, ...(freeText ? { freeText } : {}), skipped, ...(answeredBy ? { answeredBy } : {}) };
  }
}

// ---- partition ------------------------------------------------------------

/** Segment open for STEPS. A text run CLOSES a segment, so a last segment
 * already carrying prose starts a new one — this break is the owner-item-#20
 * fix: a thinking run after output text forms a NEW group rendered after the
 * text, never merged into the group before it. */
function stepSeg(t: PiTurnRow): PiTurnSegment {
  let g = t.segments[t.segments.length - 1];
  if (!g || g.textBlocks.length || g.asks.length || g.escs.length || g.cards?.length) {
    g = { key: `${t.key}-g${t.segments.length}`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
    t.segments.push(g);
  }
  return g;
}

/** Segment receiving TEXT: prose joins the current segment after its steps
 * (consecutive text runs extend the same prose slot). Once here, text is
 * prose for good — nothing ever moves it back into a step timeline. */
function textSeg(t: PiTurnRow): PiTurnSegment {
  let g = t.segments[t.segments.length - 1];
  if (!g || g.asks.length || g.escs.length || g.cards?.length) {
    // An ask-closed segment stays closed: prose after a question card is a
    // NEW section, exactly as prose closes a step run (owner item 46). Same
    // rule for an escalation-closed one.
    g = { key: `${t.key}-g${t.segments.length}`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
    t.segments.push(g);
  }
  return g;
}

/** Segment receiving ASK CARDS: asks join the current segment after its
 * steps/prose and CLOSE it — any later step or text opens the next one.
 * (An esc-closed segment is already past the ask slot — new segment, so the
 * per-segment render order steps→text→asks→escs never inverts arrival.) */
function askSeg(t: PiTurnRow): PiTurnSegment {
  let g = t.segments[t.segments.length - 1];
  if (!g || g.escs.length) {
    g = { key: `${t.key}-g${t.segments.length}`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
    t.segments.push(g);
  }
  return g;
}

/** Segment receiving ESC CARDS: same closing contract as asks; escs render
 * after asks, so an esc may join an ask-closed segment in arrival order. */
function escSeg(t: PiTurnRow): PiTurnSegment {
  let g = t.segments[t.segments.length - 1];
  if (!g) {
    g = { key: `${t.key}-g0`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
    t.segments.push(g);
  }
  return g;
}

/** Segment receiving the REFUSAL FOOTER (REL-097). The footer is written by
 * the Bridge AFTER the model finished the reply, so it renders LAST in the
 * segment it lands in and never closes anything — there is nothing after it
 * in the turn. */
function refusalSeg(t: PiTurnRow): PiTurnSegment {
  let g = t.segments[t.segments.length - 1];
  if (!g) {
    g = { key: `${t.key}-g0`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
    t.segments.push(g);
  }
  return g;
}

/** Flatten the transcript into rows. Pure — called per render pass; row keys
 * are first-message-index based and segment keys are turn-ordinal based, so a
 * growing transcript only ever APPENDS rows/segments/steps or extends the
 * last one (the append-only DOM lane depends on this). */
export function partitionRows(messages: PiMessage[]): PiRow[] {
  const rows: PiRow[] = [];
  let turn: PiTurnRow | null = null;
  /** Ask calls awaiting their result (which carries the tool-minted Q-N
   * ids) — keyed by toolCallId, scoped to this partition pass. */
  const pendingAsks = new Map<string, PiAskCard[]>();
  /** Escalate calls whose card already swallowed the receipt — their
   * toolResult must not become an orphan step row. */
  const cardedEscs = new Set<string>();
  /** Escalate receipts by call id, indexed up front: the card-vs-chip
   * decision needs the RECEIPT (a refusal keeps the chip) at call time, and
   * partitionRows sees the whole message list every pass anyway. Two-step so
   * only escalate results are parsed (results can be huge). */
  const escReceipts = new Map<string, EscalateReceipt>();
  {
    const escCallIds = new Set<string>();
    for (const m of messages) {
      if (m.role !== 'assistant' || !Array.isArray(m.content)) continue;
      for (const b of m.content as PiBlock[]) {
        if (b?.type !== 'toolCall') continue;
        const flat = b as unknown as { id?: string; name?: string };
        const name = b.toolCall?.name ?? flat.name ?? '';
        const cid = b.toolCall?.id ?? flat.id ?? '';
        if (name === 'escalate' && cid) escCallIds.add(String(cid));
      }
    }
    if (escCallIds.size) {
      for (const m of messages) {
        if (m.role !== 'toolResult' || !m.toolCallId) continue;
        const cid = String(m.toolCallId);
        if (escCallIds.has(cid)) escReceipts.set(cid, parseEscalateReceipt(textOf(m.content), m.isError === true));
      }
    }
  }

  const closeTurn = (): void => {
    if (turn) rows.push(turn);
    turn = null;
  };

  messages.forEach((m, idx) => {
    // A `custom` message that reached the builder un-normalized (a path that
    // skipped pi-live's ingest parse) gets the SAME rule as pi-live's
    // projectCustom: displayed → the user row, hidden → no row. Ordinals are
    // untouched. Inlined, not imported: the smoke transpiles this file
    // standalone (traps: instrument-copied-standalone-into-a-fixture).
    if (m.role === 'custom') {
      if (m.display !== true) return;
      m = { ...m, role: 'user' };
    }
    if (m.role === 'user') {
      closeTurn();
      // The harness's refinement result: its own card, never a user bubble.
      if (isHarnessRefined(m)) {
        rows.push({ type: 'harness-refined', key: `hr${idx}`, idx, msg: m, ...harnessRefinedOf(m) });
        return;
      }
      // The fork's other displayed outcomes (compaction, MCP connection,
      // slash-command result): a system card each, never a user bubble.
      if (isSystemOutcome(m)) {
        rows.push({ type: 'system-outcome', key: `so${idx}`, idx, msg: m, ...systemOutcomeOf(m) });
        return;
      }
      // Every OTHER displayed harness custom (python-state-restored, goal
      // context, heartbeat, child status, an extension's own): the TUI's
      // third category — an inline system notice, never a user bubble.
      if (isSystemNotice(m)) {
        rows.push({ type: 'system-notice', key: `sn${idx}`, idx, msg: m, customType: m.customType as string });
        return;
      }
      const rawText = textOf(m.content);
      // Escalation lifecycle envelopes (the coord attrs pi-live preserved at
      // ingest) render as first-class event cards; every OTHER coordination
      // event deliberately stays a plain bubble (the control case).
      const coord = m.coord;
      if (coord) {
        const ev = escEventFromCoord(coord, rawText);
        if (ev) {
          rows.push({ type: 'esc-event', key: `esc${idx}`, idx, msg: m, ...ev });
          return;
        }
      }
      // The wake executor's worklist pull — the WHOLE message is `linkup`
      // (text containing the word stays a normal bubble).
      if (rawText.trim() === 'linkup') {
        rows.push({ type: 'linkup', key: `lk${idx}`, idx, msg: m });
        return;
      }
      // Owner item 46: an injected <ask-answer> message is the ANSWER CARD
      // row, never a plain user bubble — same message, richer rendering.
      const answer = parseAskAnswer(rawText);
      if (answer) {
        rows.push({ type: 'ask-answer', key: `aa${idx}`, idx, msg: m, ...answer });
        return;
      }
      rows.push({ type: 'user', key: `u${idx}`, idx, msg: m });
      return;
    }
    if (m.role === 'error') {
      closeTurn();
      rows.push({ type: 'error', key: `e${idx}`, idx, msg: m });
      return;
    }
    const ensureTurn = (): PiTurnRow => {
      let t = turn;
      if (!t) {
        t = {
          type: 'turn',
          key: `t${idx}`,
          firstIdx: idx,
          lastIdx: idx,
          segments: [],
          attachments: [],
        };
        turn = t;
      }
      t.lastIdx = idx;
      return t;
    };
    if (m.role === 'assistant') {
      const t = ensureTurn();
      if (m.stoppedEarly === true) t.stoppedEarly = true;
      const msgTs = normTs(m.timestamp);
      const push = (s: PiStep): void => {
        if (msgTs) s.ts = msgTs;
        stepSeg(t).steps.push(s);
      };
      const blocks = Array.isArray(m.content) ? (m.content as PiBlock[]) : [];
      blocks.forEach((b, bi) => {
        if (b.type === 'thinking' && (b.thinking ?? '').trim()) {
          push(thinkingStep(b, `m${idx}-b${bi}`));
        } else if (b.type === 'toolCall') {
          // Two shapes reach here: streaming blocks nest the call under
          // b.toolCall; pi 0.84's authoritative message_end/snapshot blocks
          // are FLAT ({type:'toolCall', id, name, arguments}).
          const flat = b as unknown as { id?: string; name?: string; arguments?: unknown };
          const call: PiToolCall | undefined = b.toolCall;
          const name = call?.name ?? flat.name ?? '';
          const args = call?.arguments ?? flat.arguments ?? b.argsRaw;
          const callId = call?.id ?? flat.id ?? '';
          if (name === 'ask_user_question') {
            // Owner item 46: one FIRST-CLASS card per question, in the turn
            // flow — never a step chip. Ids arrive on the tool result.
            const cards = askCardsFrom(args, callId || `m${idx}-b${bi}`);
            if (cards.length) {
              const g = askSeg(t);
              g.asks.push(...cards);
              if (callId) pendingAsks.set(callId, cards);
              return;
            }
            // Malformed args (no parseable questions): keep the step chip so
            // the call is still VISIBLE rather than silently vanishing.
          }
          if (name === 'escalate') {
            // A raise is FIRST-CLASS in the flow, like asks. The receipt
            // decides card vs chip: a REFUSAL ("escalate: …", isError) wrote
            // nothing and keeps the plain step chip (the control); success —
            // or a still-streaming result — cards, and the receipt's ids
            // fill in (patched in place when they land on a later pass).
            const receipt = callId ? escReceipts.get(callId) : undefined;
            if (!receipt || !receipt.refused) {
              const card = escCardFrom(args, callId || `m${idx}-b${bi}`);
              if (card) {
                if (receipt) fillEscCard(card, receipt);
                escSeg(t).escs.push(card);
                if (callId) cardedEscs.add(callId);
                return;
              }
              // Malformed args: keep the step chip — visible, never vanished.
            }
          }
          push(toolStep({ id: callId || undefined, name: name || undefined, args }, `m${idx}-b${bi}`));
        } else if (b.type === 'text' && (b.text ?? '').trim()) {
          // REL-097 — the Bridge's machine-written refusal footer is a text
          // block, but it is NOT prose: it renders as its own block so a
          // reader can tell at a glance what the model wrote from what the
          // machinery wrote over it. Anything that is not a well-formed
          // footer falls through to prose (refusalCardFrom returns null),
          // which is the control against swallowing an ordinary paragraph.
          const rcard = refusalCardFrom(b.text ?? '', `m${idx}-b${bi}`);
          if (rcard) {
            refusalSeg(t).refusals.push(rcard);
            return;
          }
          const g = textSeg(t);
          g.textBlocks.push(b);
          g.textMsg = m;
        } else if (b.type === 'attachment') {
          t.attachments.push(b as unknown as PiAttachmentBlock);
        }
      });
      return;
    }
    if (m.role === 'toolResult') {
      const t = ensureTurn();
      const id = m.toolCallId ? String(m.toolCallId) : '';
      // Ask results are SWALLOWED into their cards (ids + legacy answers) —
      // never an orphan step row (the cards already stand in the flow).
      const askCards = id ? pendingAsks.get(id) : undefined;
      if (askCards) {
        pendingAsks.delete(id);
        fillAskCards(askCards, textOf(m.content), m.isError === true);
        return;
      }
      // Same for a carded escalate's receipt (already folded in via the
      // up-front index) — the card stands in the flow, no orphan step.
      if (id && cardedEscs.has(id)) {
        cardedEscs.delete(id);
        return;
      }
      let step: PiStep | undefined;
      if (id) {
        // The call is usually in the newest segment — search backwards.
        for (let gi = t.segments.length - 1; gi >= 0 && !step; gi--) {
          step = t.segments[gi]!.steps.find((s) => s.kind === 'tool' && s.toolId === id);
        }
      }
      const text = textOf(m.content);
      if (step) {
        step.resultText = text;
        if (m.details !== undefined) step.resultDetails = m.details;
        if (m.isError === true) step.isError = true;
      } else {
        // Result whose call was never seen (partial snapshot) — standalone row.
        const orphan = toolStep({ id, name: String(m.toolName ?? 'tool') }, `m${idx}-tr`);
        orphan.resultText = text;
        if (m.details !== undefined) orphan.resultDetails = m.details;
        if (m.isError === true) orphan.isError = true;
        const ts = normTs(m.timestamp);
        if (ts) orphan.ts = ts;
        stepSeg(t).steps.push(orphan);
      }
      return;
    }
    if (m.role === 'bashExecution') {
      const t = ensureTurn();
      const step = toolStep({ name: 'bash' }, `m${idx}-bash`);
      step.command = String(m.command ?? '');
      step.output = String(m.output ?? '');
      const ts = normTs(m.timestamp);
      if (ts) step.ts = ts;
      stepSeg(t).steps.push(step);
      return;
    }
    if (m.role === 'aisdlc-card') {
      // P22 lane u4 — a Helm-server display card (pi-live folds it). It JOINS
      // the turn it follows and never OPENS one, and never moves lastIdx: it
      // carries no transcript ordinal, and a turn row made of it would read as
      // an invented turn to the DOM turn reader. With no turn yet, its own row.
      const disp = (m as { display?: { mime?: unknown; data?: unknown } }).display;
      if (!disp || typeof disp.mime !== 'string') return;
      const card: PiHostCard = { key: `ac${idx}`, mime: disp.mime, data: disp.data };
      const t = turn as PiTurnRow | null;
      if (!t) {
        rows.push({ type: 'aisdlc-card', idx, msg: m, ...card });
        return;
      }
      let g = t.segments[t.segments.length - 1];
      if (!g) {
        g = { key: `${t.key}-g0`, steps: [], textBlocks: [], asks: [], escs: [], refusals: [] };
        t.segments.push(g);
      }
      (g.cards ??= []).push(card);
      return;
    }
    // Unknown roles are ignored (forward-compat with newer pi transcripts).
  });
  closeTurn();
  return rows;
}

// ---- summary line ---------------------------------------------------------

const CAT_PHRASE: Partial<Record<StepCategory, (n: number) => string>> = {
  command: (n) => `ran ${n} command${n === 1 ? '' : 's'}`,
  read: (n) => `read ${n} file${n === 1 ? '' : 's'}`,
  create: (n) => `created ${n} file${n === 1 ? '' : 's'}`,
  edit: (n) => `edited ${n} file${n === 1 ? '' : 's'}`,
  search: (n) => (n === 1 ? 'searched files' : `ran ${n} searches`),
  web: () => 'searched the web',
  fetch: (n) => `fetched ${n} page${n === 1 ? '' : 's'}`,
  agent: (n) => `ran ${n} agent${n === 1 ? '' : 's'}`,
  tasks: () => 'updated tasks',
  message: (n) => `sent ${n} message${n === 1 ? '' : 's'}`,
  team: (n) => (n === 1 ? 'ran a team action' : `ran ${n} team actions`),
};

/** The collapsed head line — "Thought for 2 min 14 sec, ran 3 commands,
 * used Query warehouse". Empty when there are no steps. Composed per
 * SEGMENT: each [steps] section of a turn folds behind its own head. */
export function composeStepsSummary(steps: PiStep[]): string {
  if (!steps.length) return '';
  const phrases: string[] = [];
  let thinkMs = 0;
  let thinkCount = 0;
  const catCounts = new Map<StepCategory, number>();
  const catOrder: StepCategory[] = [];
  const otherNames = new Map<string, number>();
  for (const s of steps) {
    if (s.kind === 'thinking') {
      thinkCount += 1;
      if (typeof s.thinkingMs === 'number') thinkMs += s.thinkingMs;
    } else if (s.kind === 'tool') {
      const cat = s.cat ?? 'other';
      if (cat === 'other') {
        const name = prettyToolName(s.toolName ?? 'tool');
        otherNames.set(name, (otherNames.get(name) ?? 0) + 1);
        if (!catOrder.includes('other')) catOrder.push('other');
      } else {
        if (!catCounts.has(cat)) catOrder.push(cat);
        catCounts.set(cat, (catCounts.get(cat) ?? 0) + 1);
      }
    }
  }
  if (thinkCount > 0) phrases.push(thinkMs > 0 ? `thought for ${fmtThinkTime(thinkMs)}` : 'thought it through');
  for (const cat of catOrder) {
    if (cat === 'other') {
      const names = [...otherNames.keys()];
      const total = [...otherNames.values()].reduce((a, b) => a + b, 0);
      if (names.length === 1) phrases.push(`used ${names[0]}${total > 1 ? ` ×${total}` : ''}`);
      else if (names.length === 2) phrases.push(`used ${names[0]} and ${names[1]}`);
      else phrases.push(`used ${total} tools`);
    } else {
      phrases.push(CAT_PHRASE[cat]!(catCounts.get(cat) ?? 0));
    }
  }
  const out = phrases.join(', ');
  return out.charAt(0).toUpperCase() + out.slice(1);
}

// ---- icons ----------------------------------------------------------------

/** 16×16 stroke icons (currentColor) for step rows + the composer. Static
 * markup only — never interpolate data into these. */
export const STEP_ICONS: Record<string, string> = {
  terminal:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="2.5" width="13" height="11" rx="2.2"/><path d="M4.4 6.2l2.4 1.8-2.4 1.8M8.6 10.4h3"/></svg>',
  file:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 1.8h4.8L12 5v9.2H4z"/><path d="M8.8 1.8V5H12"/></svg>',
  filePlus:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 1.8h4.8L12 5v9.2H4z"/><path d="M8.8 1.8V5H12M8 7.4v3.6M6.2 9.2h3.6"/></svg>',
  pencil:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M11.3 2.3l2.4 2.4L5.6 12.8l-3.1.7.7-3.1zM9.8 3.8l2.4 2.4"/></svg>',
  search:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="7" cy="7" r="4.4"/><path d="M10.4 10.4L14 14"/></svg>',
  globe:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"><circle cx="8" cy="8" r="6.2"/><path d="M1.8 8h12.4M8 1.8c1.9 1.9 1.9 10.5 0 12.4-1.9-1.9-1.9-10.5 0-12.4z"/></svg>',
  agents:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"><circle cx="4" cy="4.2" r="1.9"/><circle cx="12" cy="4.2" r="1.9"/><circle cx="8" cy="12" r="1.9"/><path d="M5.2 5.8L7 10.2M10.8 5.8L9 10.2"/></svg>',
  tasks:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M2.6 4.4l1.1 1.1 2-2.2M2.6 9.9l1.1 1.1 2-2.2M8.6 4.9h4.8M8.6 10.4h4.8"/></svg>',
  spark:
    '<svg viewBox="0 0 16 16" fill="currentColor" stroke="none"><path d="M8 1.6c.5 3.2 1.6 4.3 4.9 4.9v1c-3.3.6-4.4 1.7-4.9 4.9h-1c-.5-3.2-1.6-4.3-4.9-4.9v-1c3.3-.6 4.4-1.7 4.9-4.9z"/><path d="M12.6 10.4c.3 1.6.9 2.2 2.5 2.5v.6c-1.6.3-2.2.9-2.5 2.5h-.6c-.3-1.6-.9-2.2-2.5-2.5v-.6c1.6-.3 2.2-.9 2.5-2.5z"/></svg>',
  chat:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3.6c0-.7.5-1.2 1.2-1.2h9.6c.7 0 1.2.5 1.2 1.2v6c0 .7-.5 1.2-1.2 1.2H6.4L3.2 13.6v-2.8H3.2c-.7 0-1.2-.5-1.2-1.2z"/><path d="M4.8 5.6h6.4M4.8 8h4"/></svg>',
};

export const COMPOSER_ICONS = {
  plus:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M8 2.8v10.4M2.8 8h10.4"/></svg>',
  folder:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M1.8 4.2c0-.7.5-1.2 1.2-1.2h3l1.4 1.6h5.6c.7 0 1.2.5 1.2 1.2v6c0 .7-.5 1.2-1.2 1.2H3c-.7 0-1.2-.5-1.2-1.2z"/></svg>',
  arrowUp:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 13.2V3.4M3.8 7.2L8 3l4.2 4.2"/></svg>',
  stop:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"><rect x="4.2" y="4.2" width="7.6" height="7.6" rx="1.4"/></svg>',
  chevron:
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4.2 6.2L8 10l3.8-3.8"/></svg>',
};
